/**
 * ChargeLayer - Renders formal charges from SVG generation data
 *
 * Consumes charge data from SVGGenerationContext and renders them as SVG.
 * Default visual rendering uses DynamicCharge (motion-enabled).
 * No hitboxes — charges are not interactive.
 */

import React, { memo } from 'react';
import { useSVGGenerationData } from '../../contexts/SVGGenerationContext';
import { DynamicCharge } from '../../motion/components/DynamicCharge';
import type { ChargeData } from '../../rendering/useSVGGeneration';

export interface ChargeLayerProps {
  /** Custom render function for each charge. Defaults to DynamicCharge. */
  render?: (chargeData: ChargeData) => React.ReactNode;
}

// React.memo (PIPH-69): see AtomLayer for rationale.
export const ChargeLayer = memo(function ChargeLayer({ render }: ChargeLayerProps) {
  const { chargeData } = useSVGGenerationData();

  return (
    <g data-layer="charges">
      {chargeData.map((charge, index) => (
        <React.Fragment key={`charge-${index}`}>
          {render
            ? render(charge)
            : <DynamicCharge chargeData={charge} />
          }
        </React.Fragment>
      ))}
    </g>
  );
});
